import { ChangeDetectorRef, Component, Input, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, filter, forkJoin, map, merge, switchMap, take, takeUntil } from 'rxjs';
import { DetailsDrawerBaseComponent } from 'app/budget-object-details/components/containers/details-drawer-base';
import { BudgetSettingsPageService } from 'app/budget-settings/components/budget-settings-page/budget-settings-page.service';
import { DrawerFormFields } from '../details-drawer-form';
import { BudgetObjectDetailsService } from '../../../types/budget-object-details-service.interface';
import { ObjectDetailsTabsDataService } from 'app/budget-object-details/services/object-details-tab-data.service';
import { SelectOption } from '../../../../shared/types/select-option.interface';
import { Budget, BudgetTimeframesType } from '../../../../shared/types/budget.interface';
import { BudgetOwnerList } from '../../../../budget-settings/components/budget-settings-page/budget-settings-page.type';
import { BudgetSettingsDrawerService, ManagePageFieldsList } from './budget-settings-drawer.service';
import { BudgetObjectActionsBuilder } from '../../../services/budget-object-actions-builder.service';
import { BudgetSettingsContextService } from './BudgetSettingsContextService';
import { messages } from '../../../../budget-settings/components/budget-settings-page/budget-settings-page.utils';
import { LocalStorageService } from '../../../../../../../common-lib/src/services/local-storage.service';
import { BudgetsManagingService } from 'app/budget-settings/services/budgets-managing.service';

@Component({
  selector: 'budget-settings-drawer',
  templateUrl: './budget-settings-drawer.component.html',
  styleUrls: ['./budget-settings-drawer.component.scss'],
  providers: [
    BudgetSettingsPageService,
    ObjectDetailsTabsDataService
  ]
})
export class BudgetSettingsDrawerComponent extends DetailsDrawerBaseComponent<any> {
  protected budgetNameMaxLength = this.configuration.MAX_TEXT_INPUT_LENGTH;
  protected objectDetailsService: BudgetObjectDetailsService<any>;
  formConfig: any;
  isEditMode = false;
  ownerOptions: SelectOption[] = [];
  cegStatusEnabled = true;
  isFormValid = true;
  companyCurrency: {
    symbol: string;
    code: string;
  };

  @Input() isActive: boolean;
  handledEditMode = false;
  prevName: string;
  private wasActive = false;
  companyId: number;
  budget: Budget;
  accountOwnerId: number;
  ownersList: BudgetOwnerList[] = [];
  isForecastEnabled = false; // Default value for forecast enabled
  private cachedAccountOwnerId: number;
  private managePageFields: ManagePageFieldsList[] = [];
  managePageFieldsKeyed: Record<string, ManagePageFieldsList> = {};

  selectedFormulaNames: string[] = [];
  selectedFormulaIds: number[] = [];
  defaultSelectedFormulaNames: string[] = [];
  defaultSelectedFormulaIds: number[] = [];
  isFAB: boolean = true;
  isForecastSmartCellEnabled: boolean = false;
  canDisableForecast: boolean = true;
  
  constructor(
    private budgetSettingsPageService: BudgetSettingsPageService,
    protected fb: FormBuilder,
    private drawerService: BudgetSettingsDrawerService,
    private cdRef: ChangeDetectorRef,
    protected menuActionsBuilder : BudgetObjectActionsBuilder,
    private budgetSettingsContextService: BudgetSettingsContextService,
    private readonly budgetsManagingService: BudgetsManagingService,
  ) {
    super();
    this.setObjectType(this.configuration.OBJECT_TYPES.budget);
    this.managePageFields = LocalStorageService.getFromStorage('MANAGE_PAGE_FIELDS');
    this.managePageFieldsKeyed = this.formatByKeyCode(this.managePageFields);
  }


  public onInit(): void {
    this.isEditMode = !!this.objectId;
    this.budgetSettingsContextService.getContext$()
    .pipe(
      takeUntil(this.destroy$),
      filter(Boolean)
    )
    .subscribe((context) => {
      if (context.accountOwnerId !== this.cachedAccountOwnerId && context.ownersList.length > 0) {
        this.cachedAccountOwnerId = context.accountOwnerId;
        this.accountOwnerId = context.accountOwnerId;
        this.ownersList = context.ownersList || [];
        this.loadBudgetSettingsData(this.isEditMode);
      }
    });
  }

  private buildDefaultCABFormulaSelectionsFromList(formulaKeys: string[]) {
    const selectedNames: string[] = [];
    const selectedIds: number[] = [];

    const anchorField = this.managePageFields.find(field => field.is_default_anchor);
    if (anchorField) {
      selectedNames.push(anchorField.name);
      selectedIds.push(anchorField.display_order);
    }

    formulaKeys.forEach(key => {
      const field = this.managePageFieldsKeyed[key];
      if (field && !selectedIds.includes(field.display_order)) {
        selectedNames.push(field.name);
        selectedIds.push(field.display_order);
      }
    });

    this.defaultSelectedFormulaNames = selectedNames;
    this.defaultSelectedFormulaIds = selectedIds;
  }


  private loadBudgetSettingsData(isEditMode: boolean): void {
    const { data } = this.budgetSettingsPageService.getBudgetSettingsDetailsSync(
      this.accountOwnerId,
      this.ownersList
    );

    const buildAndSetupForm = (budgetData?: any) => {
      this.ownerOptions = data?.owners || [];
      this.companyCurrency = data?.companyCurrency;
      this.companyId = data?.companyId;

      if (isEditMode && budgetData) {
        data.budgetData = { ...budgetData };
        this.prevName = budgetData.name;
      }

      this.formData = this.fb.group(this.buildFormConfig(data));
      this.isFormValid = this.formData.valid;

      if (this.isEditMode) {
        setTimeout(() => {
          this.formData.get(DrawerFormFields.timeframeType)?.disable({ emitEvent: false });
        }, 1000);
      }

      this.cdRef.detectChanges();
      this.setupFormChangeTracking();
    };

    if (isEditMode) {
      const responseApi= this.isFAB ? this.drawerService.getFabBudgetSettings(this.objectId) : this.drawerService.getBudgetSettings(this.objectId);
      forkJoin({
        defaultKeys: this.budgetService.getDefaultBudgetsCABFormula(data?.companyId),
        response: responseApi
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ defaultKeys, response }) => {
            this.buildDefaultCABFormulaSelectionsFromList(defaultKeys);

            this.budget = { ...response.budget };
            this.isForecastEnabled = response.budget.is_forecast_enabled;
            this.cegStatusEnabled = response.budget.new_campaigns_programs_structure;
            this.isForecastSmartCellEnabled = response.budget.anchor_field === this.managePageFieldsKeyed['forecast']?.display_order;
            this.canDisableForecast = !this.isForecastSmartCellEnabled;

            if(!this.isFAB) {
              this.selectedFormulaIds = response.budget_cab_formula.map(f => f.manage_page_fields);
              this.selectedFormulaNames = response.budget_cab_formula.map(f => f.name);
            } else {
              this.selectedFormulaIds = response.budget_cab_formula.map(id => this.managePageFieldsKeyed[id]?.display_order || 0);
              this.selectedFormulaNames = response.budget_cab_formula.map(id => this.managePageFieldsKeyed[id]?.name || '');
            }

            buildAndSetupForm(this.budget);
            this.updateMenuActions();
          },
          error: (err) => {
            console.error('Error loading budget settings or default formula:', err);
          }
        });
    } else {
      this.budgetService.getDefaultBudgetsCABFormula(data?.companyId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (defaultKeys: string[]) => {
            this.buildDefaultCABFormulaSelectionsFromList(defaultKeys);

            this.selectedFormulaNames = [...this.defaultSelectedFormulaNames];
            this.selectedFormulaIds = [...this.defaultSelectedFormulaIds];

            buildAndSetupForm();
          },
          error: (err) => {
            console.error('Error loading default formula for create mode:', err);
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive && !changes.isActive.firstChange && this.isActive && !this.wasActive) {
      this.loadBudgetSettingsData(this.isEditMode);
    }

    this.wasActive = this.isActive;
  }

  formatByKeyCode(input: any[]): Record<string, any> {
    return input.reduce((acc, item) => {
      if (item.key_code) {
        acc[item.key_code] = item;
      }
      return acc;
    }, {} as Record<string, any>);
  }

  private buildFormConfig(data: any) {
    const defaultDate = new Date();
    defaultDate.setMonth(0);
    defaultDate.setDate(1);

    return {
      [DrawerFormFields.name]: [
        data?.budgetData?.name || '',
        Validators.compose([Validators.required, Validators.maxLength(this.budgetNameMaxLength)])
      ],
      [DrawerFormFields.ownerId]: [
        data?.budgetData?.owner || data.accountOwnerId,
        [Validators.required, Validators.min(0)]
      ],
      [DrawerFormFields.amount]: [data?.budgetData?.amount || 0],
      [DrawerFormFields.timeframeType]: [
        {
          value: data?.budgetData?.type ?? BudgetTimeframesType.Month,
          disabled: this.isEditMode
        },
        Validators.required
      ],
      [DrawerFormFields.startDate]: [data?.budgetData?.budget_from || defaultDate, Validators.required],
      cegStatusesEnabled: [data?.budgetData?.new_campaigns_programs_structure || false],
      forecastEnabled: [this.isEditMode ? !!data?.budgetData?.is_forecast_enabled : false]
    };
  }

  private setupFormChangeTracking(): void {
    this.formData.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(merge(this.destroy$, this.reset$))
      )
      .subscribe(() => {
        if(this.formData.get('name').value !== this.prevName) {
          this.syncUnsavedChangesFlag(true);
        }
        else{
          this.syncUnsavedChangesFlag();
        }
      });

    this.formData.statusChanges
    .pipe(
      takeUntil(merge(this.destroy$, this.reset$))
    )
    .subscribe((status: string) => {
      this.isFormValid = status === 'VALID';
    });
  }

  protected initObjectCreation(): void {
    // Your creation-specific logic, if any.
  }

  protected loadObjectDetails(id: number): void {
    // Not needed here. You can implement if Budget has detail load by ID.
  }

  protected updateMenuActions(): void {
    if (!this.isEditMode) {
      this.menuActions = [];
      return;
    }

    this.menuActionsBuilder
      .reset()
      .addCloneAction(this.objectLabel, this.handleClone.bind(this), this.isReadOnlyMode)
      .addDeleteAction(this.objectLabel, this.handleDelete.bind(this), this.isReadOnlyMode);

    this.menuActions = this.menuActionsBuilder.getActions();
  }

  private handleClone() {
    if (this.unsavedChangesFlag) {
      this.dialogManager.openUnsavedChangesDialog(
        () => {
          this.saveChanges(() => {
            this.appRoutingService.closeActiveDrawer();
            setTimeout(() => {
              this.budgetSettingsPageService.duplicateBudget(this.budget);
            }, 0);
          });
        },
        () => {
          this.syncUnsavedChangesFlag(false);
          this.budgetSettingsPageService.duplicateBudget(this.budget);
        }
      );
    } else {
      this.budgetSettingsPageService.duplicateBudget(this.budget);
    }
  }


  private handleDelete() {
    this.budgetSettingsPageService.cegBudgetDeleted$.pipe(
      takeUntil(this.destroy$),
      filter(deleted => deleted),
      take(1)
    ).subscribe(() => {
      this.syncUnsavedChangesFlag(false);
      setTimeout(() => {
        this.appRoutingService.closeActiveDrawer();
      });
    });

    this.budgetSettingsPageService.deleteBudget(this.budget);
  }

  protected formDataToState(): any {
    return this.formData.getRawValue();
  }

  handleForecastChange(val: boolean) {
    const ctrl = this.formData.get('forecastEnabled');
    if (ctrl.value !== val) {
      ctrl.setValue(val);
    }
  }


  private buildSavePayload(): any {
    const form = this.formData.getRawValue();
    const startDate: Date = new Date(form.startDate);
    const endDate = new Date(startDate);
    endDate.setFullYear(startDate.getFullYear() + 1);
    endDate.setDate(endDate.getDate() - 1);

    const payload = {
      name: form.name,
      owner: form.ownerId,
      amount: form.amount,
      type: form.timeframeType,
      budget_from: this.formatDateToYMD(startDate),
      budget_to: this.formatDateToYMD(endDate),
      is_forecast_enabled: form.forecastEnabled,
      manage_page_fields: this.selectedFormulaIds,
      company: this.companyId
    };

    if (this.isEditMode) {
      if(!this.objectId){
        this.objectId = this.budget.id; // Fallback to budget ID if not set specially for newly created company
      }
      payload['id'] = this.objectId;
    } else {
      payload['anchor_field'] = this.selectedFormulaIds[0] || 1;
      payload['create_by'] = this.accountOwnerId;
    }

    return payload;
  }

  private formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }


  public saveChanges(onSavedCb?: () => void, runInBackground?: boolean): void {
    const nameCtrl = this.formData.get('name');
    const trimmedName = nameCtrl?.value?.trim();
    nameCtrl?.setValue(trimmedName, { emitEvent: false });

    if (!this.formData.valid) {
      this.utilityService.showToast({ Title: '', Message: 'Please correct the form errors.', Type: 'error' }, true);
      return;
    }

    const payload = this.buildSavePayload();
    let save$ = (
        this.isEditMode
          ? this.drawerService.updateBudgetWithCAB(this.objectId, payload, this.isFAB)
          : this.drawerService.createBudgetWithCAB(payload, this.isFAB)
        ).pipe(
            switchMap(budget =>
              this.budgetsManagingService.updateExchangeRatesAfterAddingOrUpdatingBudget(
                this.companyId, this.companyCurrency?.code
              ).pipe(map(() => budget))
            )
          )

    save$.subscribe({
      next: (res) => {
        this.syncUnsavedChangesFlag(false);
        if (this.isEditMode) {
          this.utilityService.showToast({Title: '', Message: messages.updateBudgetSuccess, Type: 'success'});
          setTimeout(() => {
            this.budgetDataService.refreshBudgetInList(res);
            this.budgetDataService.selectBudget(res.id);
            this.budgetDataService.loadBudgetTimeframes(res);
          });
        } else {
          this.utilityService.showToast({Title: '', Message: messages.createBudgetSuccess, Type: 'success'});
          setTimeout(() => {
            this.budgetDataService.onBudgetCreated(res);
          });
        }
        onSavedCb?.();
      },
      error: (err) => {
        this.utilityService.showToast({
          Title: '',
          Message: err && err.message ? err.message[0] : 'Failed to save budget.',
          Type: 'error'
        });
      }
    });
  }


  protected getContextForNewObjectCreation(): any {
    return null; // Return your creation context if needed
  }

  protected initHierarchy(state: any): void {
    // If budgets have hierarchy logic, implement here.
  }

  protected checkTagsLeftover(): void {
    // Not needed for budget settings usually.
  }

  onOwnerChanged(ownerId: number) {
    this.formData.get('ownerId').setValue(ownerId);
  }

  onTimeframeTypeChanged(type: string) {
    this.formData.get('timeframeType').setValue(type);
  }

  onStartDateChanged(date: Date) {
    const ctrl = this.formData.get('startDate');
    if (ctrl && ctrl.value && new Date(ctrl.value).getTime() !== new Date(date).getTime()) {
      ctrl.setValue(date);
    }
  }

  onCABFormulaChanged({ ids }: { ids: number[] }) {
    this.selectedFormulaIds = ids;
  }

}